<template>
  <div class="wiz" :class="{ wizPage: layout === 'page' }">
    <div class="wizHead">
      <!-- On the page, the row's name is the title. -->
      <span v-if="layout !== 'page'" class="wizTitle">{{ title }}</span>
      <span v-if="stepLabel" class="wizStep">{{ stepLabel }}</span>
      <div v-if="totalSteps > 1" class="dots" aria-hidden="true">
        <span
          v-for="n in totalSteps"
          :key="n"
          class="dot"
          :class="{ on: n <= currentStep, current: n === currentStep }"
        />
      </div>
    </div>

    <div class="wizBody">
      <slot />
    </div>

    <div class="wizFoot">
      <slot name="footer" />
    </div>

    <p v-if="showAutosave" class="wizAutosave">
      Progress saves automatically — leave anytime, nothing is lost.
    </p>
  </div>
</template>

<script setup lang="ts">
// Shared chrome for the two wizards (design 1c / 1k): serif title, step label,
// dot strip, a footer slot for the step buttons, and the autosave reassurance.
withDefaults(
  defineProps<{
    title: string
    stepLabel?: string
    currentStep: number
    totalSteps: number
    showAutosave?: boolean
    // 'page' relaxes the drawer's bounded-height flex model so the wizard flows
    // at natural height inside a scrolling page (the Phone Settings page).
    layout?: 'drawer' | 'page'
  }>(),
  { showAutosave: true, layout: 'drawer' },
)
</script>

<style scoped>
.wiz { display: flex; flex-direction: column; gap: 18px; min-height: 0; flex: 1; overflow: hidden; }
/* Page layout: drop the drawer's height-bounding so the page scrolls, not the wizard body. */
.wizPage { flex: none; min-height: auto; overflow: visible; }
.wizPage .wizBody { flex: none; min-height: auto; overflow-y: visible; }
.wizHead { display: flex; flex-direction: column; gap: 8px; flex-shrink: 0; }
.wizTitle { font-family: var(--font-display); font-size: 22px; font-weight: var(--fw-semibold); letter-spacing: -0.01em; }
.wizStep { font-size: var(--fs-sm); font-weight: var(--fw-medium); color: var(--color-text-secondary); }
.dots { display: flex; align-items: center; gap: 6px; }
.dot { width: 7px; height: 7px; border-radius: var(--radius-full); background: var(--color-bg-sunk); }
.dot.on { background: var(--color-accent); }
.dot.current { box-shadow: 0 0 0 3px var(--color-accent-soft); }

.wizBody { flex: 1; min-height: 0; overflow-y: auto; display: flex; flex-direction: column; gap: var(--space-4); padding-right: 2px; }

.wizFoot { display: flex; align-items: center; gap: var(--space-3); flex-shrink: 0; }
.wizAutosave { margin: 0; font-size: var(--fs-xs); line-height: var(--lh-normal); color: var(--color-text-tertiary); flex-shrink: 0; }
</style>
